import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import dayjs from 'dayjs';
import {
  Card,
  Container,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  useMediaQuery,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { CustomIconButton, DefaultContainedButton } from '../../../utils/Buttons';
import { LoadingUI } from '../../../utils/LayoutStyles';
import { CommonLabel } from '../../../utils/Common';
import API from '../../../api';

export default function BankTransfers() {
  const navigate = useNavigate();
  const is600Screen = useMediaQuery('(min-width: 600px)');

  const [isTransfersLoading, setIsTransfersLoading] = useState(true);
  const [transfersList, setTransfersList] = useState([]);
  const [displayedTransfers, setDisplayedTransfers] = useState([]);
  const [userEntities, setUserEntities] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState('');
  const [selectedEntityAuth, setSelectedEntityAuth] = useState(null);
  const [bankData, setBankData] = useState({});

  useEffect(() => {
    getCumulativeTransfers();
    getUserEntities();
    getBankPaymentInfo();
  }, []);

  const getCumulativeTransfers = () => {
    API.get(`/bankcumulative`)
      .then((response) => {
        setTransfersList(response.data.data);
        setIsTransfersLoading(false);
      })
      .catch((error) => {
        setIsTransfersLoading(false);
      });
  };

  useEffect(() => {
    const filteredTransfers = transfersList.filter((transfer) => transfer.entity.id === selectedEntity);
    setDisplayedTransfers(filteredTransfers);
  }, [transfersList, selectedEntity]);

  const getBankPaymentInfo = () => {
    API.get(`/paymentbank`).then((response) => {
      setBankData(response.data[0]);
    });
  };

  const getUserEntities = () => {
    if (userEntities.length === 0) {
      API.get(`/userauth/entity_for_object/BANK-CUMULATIVE`).then((response) => {
        const combinedEntities = combineEntitiesByName(response.data);
        setUserEntities(combinedEntities);
      });
    }
  };

  const handleChangeSelectedEntity = (event) => {
    setSelectedEntity(event.target.value);
  };

  useEffect(() => {
    if (selectedEntity !== '') {
      const entityFound = userEntities.find((entity) => entity.entity_id === selectedEntity);
      setSelectedEntityAuth(entityFound.type);
    }
  }, [selectedEntity]);

  const handleCreateNewTransfer = () => {
    API.post(`/bankcumulative?entity_id=${selectedEntity}&payment_bank_id=${bankData.id}`).then((response) => {
      getCumulativeTransfers();
    });
  };

  const combineEntitiesByName = (entities) => {
    const typePriority = { W: 3, D: 2, R: 1 };

    const combinedEntities = entities.reduce((acc, entity) => {
      const existingEntity = acc.find((e) => e.entity_id === entity.entity_id);
      if (existingEntity) {
        if (typePriority[entity.type] > typePriority[existingEntity.type]) {
          existingEntity.type = entity.type;
          existingEntity.description = entity.description;
        }
      } else {
        acc.push({ ...entity });
      }
      return acc;
    }, []);

    return combinedEntities;
  };

  return (
    <>
      <Helmet>
        <title>Bank Transfers</title>
      </Helmet>

      <Container maxWidth="xl">
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Stack
              direction={is600Screen ? 'row' : 'column'}
              justifyContent={is600Screen ? 'space-between' : 'start'}
              alignItems={is600Screen ? 'center' : 'start'}
              gap={is600Screen ? '0' : '8px'}
            >
              <FormControl
                sx={{
                  width: '200px',
                  '& label.Mui-focused': {
                    color: '#212b36',
                  },
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: '#212b36',
                    },
                  },
                }}
                size="small"
              >
                <InputLabel id="select-label">Entità</InputLabel>
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  value={selectedEntity}
                  label="Entità"
                  onChange={handleChangeSelectedEntity}
                >
                  <MenuItem value="">
                    <em>Nessuna Entità</em>
                  </MenuItem>
                  {userEntities.map((entity) => (
                    <MenuItem key={entity.entity_id} value={entity.entity_id}>
                      {entity.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <DefaultContainedButton
                disabled={selectedEntity === '' || selectedEntityAuth !== 'W'}
                startIcon={<AddIcon />}
                onClick={() => handleCreateNewTransfer()}
              >
                Nuovo Bonifico Cumulativo
              </DefaultContainedButton>
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <Card sx={{ padding: '24px' }}>
              {selectedEntity === '' ? (
                <Typography sx={{ textAlign: 'center' }}>Selezionare un'entità</Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>N° Bonifico Cumulativo</TableCell>
                        <TableCell>Data Creazione</TableCell>
                        <TableCell>Importo</TableCell>
                        <TableCell>Valuta</TableCell>
                        <TableCell>Stato</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>

                    {!isTransfersLoading ? (
                      <TableBody>
                        {displayedTransfers.length > 0 ? (
                          displayedTransfers.map((transfer) => (
                            <TableRow hover key={transfer.id}>
                              <TableCell>{transfer.cumulative_id}</TableCell>
                              <TableCell>{dayjs(transfer.creation_date).format('DD/MM/YYYY')}</TableCell>
                              <TableCell>{transfer.total_amount}</TableCell>
                              <TableCell>{transfer.currency}</TableCell>
                              <TableCell>
                                <CommonLabel
                                  textColor={transfer.accepted ? '#118d57' : '#b71d18'}
                                  bgColor={transfer.accepted ? '#d3efdf' : '#f7ddd8'}
                                  text={transfer.accepted ? 'Accettato' : 'In Attesa'}
                                />
                              </TableCell>
                              {selectedEntityAuth === 'W' ? (
                                <TableCell>
                                  <CustomIconButton
                                    onClick={() =>
                                      navigate('/admin/bank-transfers/details', {
                                        state: { transferID: transfer.id, transferName: transfer.entity.name },
                                      })
                                    }
                                  >
                                    <KeyboardArrowRightIcon />
                                  </CustomIconButton>
                                </TableCell>
                              ) : (
                                <TableCell />
                              )}
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={6} sx={{ textAlign: 'center' }}>
                              Nessun dato disponibile
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    ) : (
                      <TableBody>
                        <TableRow>
                          <TableCell colSpan={4} sx={{ textAlign: 'center' }}>
                            {LoadingUI()}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    )}
                  </Table>
                </TableContainer>
              )}
            </Card>
          </Grid>
        </Grid>
      </Container>
    </>
  );
}
